import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Truck, Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [email, setEmail] = useState("admin@logihr.com");
  const [password, setPassword] = useState("Admin123!");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(t("success"));
      nav("/");
    } else {
      toast.error(res.error || t("error"));
    }
  };

  const demos = [
    { label: "Admin", email: "admin@logihr.com", pw: "Admin123!" },
    { label: "İK / HR", email: "hr@logihr.com", pw: "Hr12345!" },
    { label: "Manager", email: "manager@logihr.com", pw: "Manager123!" },
    { label: "Driver", email: "driver@logihr.com", pw: "Driver123!" },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 stripe-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative min-h-screen grid lg:grid-cols-2">
        {/* Left: branding */}
        <div className="hidden lg:flex flex-col justify-between p-10 border-r border-border">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center shadow-xl">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-xl leading-none">LogiHR Global</div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{t("tagline")}</div>
            </div>
          </div>

          <div className="space-y-4 max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card/60 text-xs font-mono-brand uppercase tracking-wider text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Enterprise HR Suite
            </div>
            <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
              {t("heroTitle")}
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">{t("heroDesc")}</p>
            <div className="grid grid-cols-2 gap-3 pt-4">
              {["SRC & ADR", "Takograf", "Pasaport & Vize", "Psikoteknik", "Bordro & Puantaj", "AI Sözleşme"].map((x, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span className="font-mono-brand text-xs uppercase tracking-wider text-muted-foreground">{x}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-muted-foreground font-mono-brand uppercase tracking-wider">
            © LogiHR Global · International Transport HRM
          </div>
        </div>

        {/* Right: form */}
        <div className="flex items-center justify-center p-6 lg:p-10">
          <div className="w-full max-w-md space-y-6">
            <div className="flex items-center justify-between">
              <div className="lg:hidden flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div className="font-display font-extrabold">LogiHR</div>
              </div>
              <button
                data-testid="btn-login-lang"
                onClick={() => setLang(lang === "tr" ? "en" : "tr")}
                className="text-xs font-mono-brand uppercase tracking-wider text-muted-foreground hover:text-foreground"
              >
                {lang === "tr" ? "EN" : "TR"}
              </button>
            </div>

            <div>
              <h2 className="font-display text-3xl font-extrabold tracking-tight">{t("login")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("signInWelcome")}</p>
            </div>

            <Card className="p-6 border-border/60">
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("email")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="email" data-testid="input-login-email" type="email" required
                           value={email} onChange={e => setEmail(e.target.value)}
                           className="pl-9" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="password" data-testid="input-login-password" type="password" required
                           value={password} onChange={e => setPassword(e.target.value)}
                           className="pl-9" />
                  </div>
                </div>
                <Button data-testid="btn-login-submit" type="submit" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("login")} <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </form>
            </Card>

            <div className="space-y-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wider font-mono-brand">{t("tryDemo")}</div>
              <div className="grid grid-cols-2 gap-2">
                {demos.map(d => (
                  <button
                    key={d.email}
                    data-testid={`btn-demo-${d.label.toLowerCase().replace(/\s|\//g,'-')}`}
                    onClick={() => { setEmail(d.email); setPassword(d.pw); }}
                    className="text-left px-3 py-2 rounded-md border border-border/60 hover:border-primary/50 hover:bg-accent transition-colors"
                  >
                    <div className="text-xs font-semibold">{d.label}</div>
                    <div className="text-[10px] font-mono-brand text-muted-foreground truncate">{d.email}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
